/** @file Keeps each project's open animation across a reload: saved per project, reopened with its document. */
import { playhead } from '../../playback/playhead';
import { currentDoc } from '../../stores/currentDoc';
import { docStore } from '../../stores/docStore';
import { playheadStore } from '../../stores/playheadStore';

/** The slice of Web Storage used (localStorage in the app; a fake in tests). */
export type AnimationStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const keyOf = (projectId: string): string => `pose2d.openAnimation.${projectId}`;

/** Storage can throw (quota, privacy mode): remembering is a convenience, so a failure is ignored. */
function attempt<T>(run: () => T, fallback: T): T {
  try {
    return run();
  } catch {
    return fallback;
  }
}

/**
 * Walkthrough D8. When a project's document arrives with no animation open, the one saved for that project is
 * reopened (if the document still has it); afterwards every change of the open animation is saved. The playhead
 * reset of a project load (before its document) is never saved. Returns the stop.
 */
export function rememberAnimation(storage: AnimationStorage | null): () => void {
  if (storage === null) return () => undefined;
  let settled: string | null = null;
  const restore = (): void => {
    const state = docStore.getState();
    const doc = currentDoc(state);
    if (state.projectId === null || doc === null) {
      settled = null;
      return;
    }
    if (settled === state.projectId) return;
    settled = state.projectId;
    const key = keyOf(state.projectId);
    const saved = attempt(() => storage.getItem(key), null);
    if (playheadStore.getState().animation === null && saved !== null && doc.animations[saved] !== undefined) playhead.setAnimation(saved);
  };
  const stopDoc = docStore.subscribe(restore);
  const stopPlayhead = playheadStore.subscribe((next, prev) => {
    const { projectId } = docStore.getState();
    if (next.animation === prev.animation || projectId === null || settled !== projectId) return;
    const key = keyOf(projectId);
    const { animation } = next;
    attempt(() => { if (animation === null) storage.removeItem(key); else storage.setItem(key, animation); }, undefined);
  });
  restore();
  return () => { stopDoc(); stopPlayhead(); };
}
